import { DownloadIcon } from 'lucide-react';
import { Button } from '../../Button';
import { PageSizeSelect, PaginationCounter, Paginator } from '../../Paginator';
import { Tooltip } from '../../Tooltip';
import { cn } from '../../../lib/utils';
import useI18n from '../../../hooks/useI18n';

export type FooterProps = {
  /** Current page, 1-based. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  /** Rows shown per page. */
  pageSize: number;
  /**
   * Total rows across all pages. Drives the counter and hides the whole footer
   * content when there's nothing to page through (<= 1 row).
   */
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  rowsPerPageOptions?: number[];
  /**
   * Downloads the filtered/sorted rows as CSV. When omitted, no export button
   * is shown.
   */
  exportTableData?: () => void;
  /** When true, the export button renders disabled (export not permitted). */
  disableExport?: boolean;
  isFullscreen?: boolean;
  fullscreenContainer?: HTMLDivElement | null;
  paginationLabel?: string;
  /** Table id — wires the paginator's `aria-controls` to the table element. */
  id?: string;
};

/**
 * Shared table footer: export button, page counter, page-size select and
 * paginator. Presentational and pagination-agnostic — the caller owns the
 * pagination state (TanStack's in `Table`, manual state in `CardList`) and
 * passes it in, so both surfaces render an identical footer.
 */
export function Footer({
  page,
  pageCount,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions,
  exportTableData,
  disableExport,
  isFullscreen = false,
  fullscreenContainer,
  paginationLabel,
  id,
}: FooterProps) {
  const { t } = useI18n();

  return (
    <div
      className={cn(
        'eye:my-8 eye:grid eye:grid-cols-3 eye:items-center eye:justify-between eye:gap-y-12',
        isFullscreen && 'eye:px-8 eye:pb-8',
      )}
    >
      <div>
        {totalItems > 0 && exportTableData && (
          <Tooltip content={t('exportAllRows')}>
            <Button
              aria-label={t('exportAllRows')}
              disabled={disableExport}
              variant='ghost'
              size='icon'
              onClick={() => exportTableData()}
            >
              <DownloadIcon aria-hidden='true' />
            </Button>
          </Tooltip>
        )}
      </div>

      {totalItems > 1 && (
        <>
          <div className='eye:flex eye:justify-center'>
            <PaginationCounter
              className='eye:hidden eye:md:block'
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
            />
          </div>

          <PageSizeSelect
            className='eye:justify-end'
            value={pageSize}
            options={rowsPerPageOptions}
            onValueChange={onPageSizeChange}
            container={isFullscreen ? fullscreenContainer : undefined}
          />

          <Paginator
            className='eye:col-span-3'
            page={page}
            pageCount={pageCount}
            onPageChange={onPageChange}
            aria-label={paginationLabel}
            aria-controls={id ? `${id}-table` : undefined}
          />
        </>
      )}
    </div>
  );
}
